import { ZOHO_BOOKS_IMPORT_PATH } from './importRun';

/**
 * 🔒 Confirm Stock (piece 31; decision 8260): a read of today's stock on hand for every active Zoho item, beside the
 * count here, that writes nothing. The Items step offers it after the Items read and after each documents read's
 * true-up, as the read a person makes before trusting the counts.
 */
export const ZOHO_BOOKS_CONFIRM_STOCK_PATH = `${ZOHO_BOOKS_IMPORT_PATH}/confirm-stock`;

/** How one item's two counts stand: alike, apart, no item here, or no count from Zoho. */
export const STOCK_CONFIRM_STATES = ['Differs', 'Missing', 'NotCounted', 'Agrees'] as const;
export type StockConfirmState = (typeof STOCK_CONFIRM_STATES)[number];

/** One active Zoho item's line: Zoho's count, the count here, the difference, and whether its row was written. */
export interface StockConfirmRow {
  zohoItemId: string;
  name: string;
  sku: string | null;
  /** The item here its row was written to, where one was. */
  itemId: string | null;
  written: boolean;
  /** Null where Zoho named no count for the item. */
  zohoCount: number | null;
  /** Null where no item here carries the Zoho item. */
  hereCount: number | null;
  /** Zoho's count less the count here; null where either is missing. */
  difference: number | null;
  state: StockConfirmState;
}

/** 🔒 The one-line total (decision 8260): the items read, the items here, and how many agree, differ and are missing. */
export interface StockConfirmTotal {
  read: number;
  here: number;
  agreeing: number;
  differing: number;
  missing: number;
}

export interface StockConfirmAnswer {
  /** When the counts were read, an instant. */
  checkedAt: string;
  rows: StockConfirmRow[];
  total: StockConfirmTotal;
}

/** 🔒 Where an item's line stands (decision 8260): no item here is Missing, no count from Zoho is NotCounted, and otherwise the two counts agree or differ. */
export function stockConfirmState(written: boolean, zohoCount: number | null, hereCount: number | null): StockConfirmState {
  if (!written) return 'Missing';
  if (zohoCount === null || hereCount === null) return 'NotCounted';
  return Math.round((zohoCount - hereCount) * 10_000) === 0 ? 'Agrees' : 'Differs';
}

/** 🔒 The list's order (decision 8260): the items that differ, then the unwritten, then the uncounted, then the agreeing, each by SKU and name. */
export function stockConfirmOrder(rows: readonly StockConfirmRow[]): StockConfirmRow[] {
  const rank = (row: StockConfirmRow) => STOCK_CONFIRM_STATES.indexOf(row.state);
  const label = (row: StockConfirmRow) => `${row.sku ?? ''}\u0000${row.name}`;
  return [...rows].sort((a, b) => rank(a) - rank(b) || (label(a) < label(b) ? -1 : label(a) > label(b) ? 1 : 0));
}

/** 🔒 The total over the lines (decision 8260). */
export function stockConfirmTotal(rows: readonly StockConfirmRow[]): StockConfirmTotal {
  return {
    read: rows.length,
    here: rows.filter((row) => row.written).length,
    agreeing: rows.filter((row) => row.state === 'Agrees').length,
    differing: rows.filter((row) => row.state === 'Differs').length,
    missing: rows.filter((row) => row.state === 'Missing').length,
  };
}
